import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import MentionDetailPage from './pages/MentionDetailPage.jsx';
import ReportsPage from './pages/ReportsPage.jsx';
import SentimentDashboardPage from './pages/SentimentDashboardPage.jsx';
import SentimentReportsPage from './pages/SentimentReportsPage.jsx';
import SubmissionDetailPage from './pages/SubmissionDetailPage.jsx';
import UpgradeAnalyticsPage from './pages/UpgradeAnalyticsPage.jsx';

const XM_ADMIN_ROLES = ['xm_admin', 'super_admin'];
const MARKETING_ROLES = ['marketing_admin', 'super_admin'];

/** The landing route for a user, based on which dashboard they belong to. */
function homePath(activeDashboard) {
  return activeDashboard === 'marketing' ? '/sentiment' : '/dashboard';
}

function FullScreenLoader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-muted)',
      }}
    >
      Loading…
    </div>
  );
}

/**
 * Gate a route on authentication and, optionally, role. `allow` is the list
 * of roles permitted here; a signed-in user without one of them is bounced
 * to their own dashboard rather than shown a page they can't use.
 */
function ProtectedRoute({ children, allow }) {
  const { isAuthenticated, bootstrapping, role, activeDashboard } = useAuth();
  const location = useLocation();
  if (bootstrapping) return <FullScreenLoader />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (allow && !allow.includes(role)) {
    return <Navigate to={homePath(activeDashboard)} replace />;
  }
  return children;
}

/** Root path: send authenticated users to their dashboard, others to login. */
function HomeRedirect() {
  const { isAuthenticated, bootstrapping, activeDashboard } = useAuth();
  if (bootstrapping) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={homePath(activeDashboard)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allow={XM_ADMIN_ROLES}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submissions/:id"
          element={
            <ProtectedRoute allow={XM_ADMIN_ROLES}>
              <SubmissionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute allow={XM_ADMIN_ROLES}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sentiment"
          element={
            <ProtectedRoute allow={MARKETING_ROLES}>
              <SentimentDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sentiment/reports"
          element={
            <ProtectedRoute allow={MARKETING_ROLES}>
              <SentimentReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mentions/:id"
          element={
            <ProtectedRoute allow={MARKETING_ROLES}>
              <MentionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allow={MARKETING_ROLES}>
              <UpgradeAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
